import { Component, OnDestroy } from '@angular/core';
import { FormControl, Validators } from "@angular/forms";
import { Utils } from "ng/src/app/core/utils";
import { createSentinelDoor, RequiredSentinelFacility, SentinelDoor } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provision.model";
import { SentinelProvisioningService } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provisioning.service";
import { FormControlMap } from "ng/src/app/shared/model/app.model";
import { Observable, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";

@Component({
  selector: 'door-editor',
  templateUrl: './door-editor.component.html',
  styleUrls: ['./door-editor.component.scss']
})
export class DoorEditorComponent implements OnDestroy {
  
  readonly formMap: Observable<DoorEditorForm | undefined>;
  errorMessage?: string;
  
  private _destroy$ = new Subject<void>();
  
  constructor(
    public sentinelProvisioningService: SentinelProvisioningService
  ) {
    this.formMap = this.sentinelProvisioningService.editingDoor.pipe(
      takeUntil(this._destroy$),
      filter(Utils.isNotNullOrUndefined),
      map((door) => {
        return this.createFormControlMap(door);
      })
    );
  }
  
  validate(formMap: DoorEditorForm) {
    const nameControl = formMap.accessPointName;
    nameControl.markAsTouched();
    nameControl.markAsDirty();
    
    nameControl.updateValueAndValidity();
    
    const errors = nameControl.errors;
    if (errors) {
      this.errorMessage = 'There are missing values.';
    }
    
    return errors;
  }
  
  stopEditing() {
    this.sentinelProvisioningService.editingDoor.next(undefined);
  }

  createFormControlMap(door: SentinelDoor): DoorEditorForm {
    const filledDoor = {
      ...createSentinelDoor(),
      ...door
    }
    
    return Object.entries(filledDoor).reduce((form, [key, value]) => {
      const k = key as keyof SentinelDoor;
      const control = new FormControl(value);
      
      if (k === 'accessPointName') {
        control.addValidators(Validators.required);
      }
      
      form[k] = control as any;
      return form;
    }, {} as DoorEditorForm);
  }
  
  async save(facility: Pick<RequiredSentinelFacility, 'facilityName' | 'id'>, formMap: DoorEditorForm): Promise<void> {
    const errors = this.validate(formMap);
    
    if (errors) {
      return Promise.reject('Invalid name');
    }
    
    const door = Object.entries(formMap).reduce((door, [key, control]) => {
      const k = key as keyof SentinelDoor;
      door[k] = control.value as never;
      return door;
    }, createSentinelDoor());
    
    await this.sentinelProvisioningService.saveDoor(facility, door, true);
  }
  
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}

type DoorEditorForm = FormControlMap<Required<SentinelDoor>>;
